import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { useReportOutput } from "@/profitability/query";
import { recordId, recordTitle } from "@/profitability/records/objects";
import { profitabilityTemplateSettingsPath } from "@/profitability/routes";
import type { ReportDates } from "@/profitability/types";
import type { FigureCursor } from "./figure-tree";
import { useCitations, useFieldCitations } from "./query";
import { SourceDocument } from "./source-document";
import { SourceFigures } from "./source-figures";
import { figureParts, sourcedColumns } from "./sourced";
import { SplitPanes } from "./split-panes";
import type { SourceTarget } from "./types";

/**
 * Where a record's figures come from: on the right, each figure that can
 * say how it was made ("Sum of 2 lines in Payroll export", "Labor × Burden
 * rate"), its parts under it when open; on the left, the files the chosen
 * figure (or part) came from, a tab each, with all its rows highlighted and
 * the chosen part's cell outlined. Choosing a figure, or moving with the
 * keys, moves the file. A large dialog rather than the Documents tab, so
 * the record stays where it was under it.
 */
export const SourceDialog = ({
  businessId,
  reportId,
  runId,
  compare,
  target,
  upload,
  open,
  container,
  onClose,
  onClosed,
}: {
  businessId: string;
  reportId: string;
  runId: string;
  compare: string | null;
  target: SourceTarget;
  /** Where a figure nothing matched is uploaded; null when it cannot be. */
  upload: { dates: ReportDates | null } | null;
  open: boolean;
  container: HTMLDialogElement | null;
  onClose: () => void;
  /** After it has animated out. */
  onClosed: () => void;
}) => {
  const { object, row } = target;
  const { dataset } = object;
  const fields = sourcedColumns(dataset);
  // A cited dataset with no sourced column lists the record itself.
  const entries = fields.length ? fields : [null];
  const startKey =
    fields.find((column) => column.key === target.column)?.key ??
    fields[0]?.key ??
    "";
  // The provider mounts a dialog per opening, so this starts on its figure,
  // open.
  const [active, setActive] = useState<FigureCursor>({
    key: startKey,
    part: null,
  });
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set([startKey]),
  );
  const id = recordId(dataset, row);
  const results = useFieldCitations(
    businessId,
    runId,
    entries.map((column) => ({
      dataset: dataset.id,
      recordId: id,
      column: column?.key ?? null,
    })),
  );
  const index = Math.max(
    entries.findIndex((column) => (column?.key ?? "") === active.key),
    0,
  );
  const column = entries[index] ?? null;
  const part =
    active.part === null
      ? null
      : figureParts(results[index]?.data)[active.part];
  // A part that is another figure shows that figure's files.
  const operand = part?.figure
    ? (dataset.columns.find((item) => item.key === part.figure) ?? null)
    : null;
  const shown = useCitations(
    businessId,
    runId,
    dataset.id,
    id,
    (operand ?? column)?.key ?? null,
  );
  const output = useReportOutput(businessId, runId);
  const [params] = useSearchParams();
  const period = params.get("period");
  const settingsHref = `${profitabilityTemplateSettingsPath(reportId)}${period ? `?${new URLSearchParams({ period }).toString()}` : ""}`;
  const list = useRef<HTMLUListElement>(null);
  const title = recordTitle(object, row);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onOpenChangeComplete={(next) => !next && onClosed()}
    >
      <DialogContent
        container={container ?? undefined}
        // Near the whole window: a file needs the room.
        className="flex h-[85vh] w-[90vw] max-w-[90vw] flex-col gap-0 overflow-hidden p-0 sm:max-w-[90vw]"
        initialFocus={() =>
          list.current?.querySelector<HTMLElement>('[tabindex="0"]') ?? true
        }
      >
        <Stack
          gap={0.5}
          className="flex-none border-b border-line py-3 pr-12 pl-5"
        >
          <DialogTitle className="truncate">
            Where {title}&apos;s figures come from
          </DialogTitle>
          <DialogDescription className="text-xs">
            How each figure was made, and the rows of the files it came from.
            Choose a figure, or one of its parts, to open the file there.
          </DialogDescription>
        </Stack>
        <SplitPanes
          label="Resize the files and the figures"
          left={
            <SourceDocument
              businessId={businessId}
              reportId={reportId}
              runId={runId}
              compare={compare}
              column={operand ?? column}
              answer={shown}
              partIndex={operand ? null : active.part}
              settingsHref={settingsHref}
              onLeave={onClose}
            />
          }
          right={
            <SourceFigures
              ref={list}
              reportId={reportId}
              runId={runId}
              entries={entries}
              columns={dataset.columns}
              row={row}
              results={results}
              active={active}
              expanded={expanded}
              settings={output.data?.settings ?? []}
              settingsHref={settingsHref}
              upload={upload}
              onActive={setActive}
              onExpand={(key, isOpen) =>
                setExpanded((current) => {
                  const next = new Set(current);
                  if (isOpen) next.add(key);
                  else next.delete(key);
                  return next;
                })
              }
              onOpen={(key) => setExpanded(new Set([key]))}
              onLeave={onClose}
            />
          }
        />
      </DialogContent>
    </Dialog>
  );
};
